import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, PackageCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSalesOrder } from "@/features/sales-orders/api";
import { canFulfill } from "@/features/sales-orders/constants";
import {
  openFulfillLines,
  remainingQty,
  remainingWeight,
  useFulfillSalesOrder,
  useMatchingBatchesForLine,
  useSalesOrderFulfillments,
  type FulfillAllocationInput,
} from "@/features/sales-orders/fulfillment-api";
import type { SalesOrderLine } from "@/features/sales-orders/types";
import { blockNumberWheel } from "@/components/shared/line-grid";

export function SalesOrderFulfillmentPage({ soId }: { soId: string }) {
  return (
    <ErrorBoundary title="Fulfillment crashed">
      <FulfillmentInner soId={soId} />
    </ErrorBoundary>
  );
}

function FulfillmentInner({ soId }: { soId: string }) {
  const navigate = useNavigate();
  const { data: so, isLoading, error } = useSalesOrder(soId);
  const { data: history = [] } = useSalesOrderFulfillments(soId);
  const fulfill = useFulfillSalesOrder();

  const [staged, setStaged] = useState<FulfillAllocationInput[]>([]);
  const [activeLineId, setActiveLineId] = useState<string>("");
  const [batchId, setBatchId] = useState("");
  const [weight, setWeight] = useState("");
  const [qty, setQty] = useState("");

  const openLines = useMemo(() => openFulfillLines(so?.lines ?? []), [so?.lines]);
  const activeLine = openLines.find((l) => l.id === activeLineId) ?? openLines[0] ?? null;

  useEffect(() => {
    if (openLines.length && !activeLineId) {
      setActiveLineId(openLines[0].id);
    }
  }, [openLines, activeLineId]);

  const { data: matchingBatches = [] } = useMatchingBatchesForLine(
    activeLine
      ? {
          material_id: activeLine.material_id,
          material_grade_id: activeLine.material_grade_id,
          shape_size_id: activeLine.shape_size_id,
        }
      : { material_id: "", material_grade_id: null, shape_size_id: null },
  );

  const totalStagedWeight = staged.reduce((s, a) => s + a.weight, 0);

  const addToStage = useCallback(() => {
    if (!activeLine) {
      toast.error("Select an order line");
      return;
    }
    if (!batchId) {
      toast.error("Select a batch");
      return;
    }
    const wt = Number(weight) || 0;
    const q = Number(qty) || 0;
    if (wt <= 0 && q <= 0) {
      toast.error("Enter weight or quantity");
      return;
    }
    setStaged((prev) => [
      ...prev,
      {
        sales_order_line_id: activeLine.id,
        inventory_batch_id: batchId,
        weight: wt,
        quantity: q,
      },
    ]);
    setBatchId("");
    setWeight("");
    setQty("");
    toast.message("Added to queue");
  }, [activeLine, batchId, weight, qty]);

  const postFulfillment = useCallback(async () => {
    if (!staged.length) {
      toast.error("Stage at least one allocation");
      return;
    }
    const result = await fulfill.mutateAsync({ salesOrderId: soId, allocations: staged });
    setStaged([]);
    navigate({ to: "/sales-orders/$soId", params: { soId } });
    void result;
  }, [staged, fulfill, soId, navigate]);

  useDocumentSaveShortcut(() => postFulfillment(), fulfill.isPending);

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !so) {
    return (
      <EmptyState
        title="Sales order not found"
        description={(error as Error)?.message}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/sales-orders">Back</Link>
          </Button>
        }
      />
    );
  }

  if (!canFulfill(so.status)) {
    return (
      <EmptyState
        title="Not open for fulfillment"
        description={`Order is ${so.status}. Confirm and start before allocating stock.`}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/sales-orders/$soId" params={{ soId }}>
              View order
            </Link>
          </Button>
        }
      />
    );
  }

  if (!openLines.length) {
    return (
      <EmptyState
        title="All lines fulfilled"
        description="Every line on this order is fully allocated."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/sales-orders/$soId" params={{ soId }}>
              View order
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <DocumentPage>
      <DocumentHeader
        title={`Fulfill ${so.so_number}`}
        back={
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/sales-orders/$soId" params={{ soId }}>
              <ArrowLeft />
            </Link>
          </Button>
        }
        actions={
          <Button
            type="button"
            size="sm"
            disabled={!staged.length || fulfill.isPending}
            onClick={() => postFulfillment()}
          >
            {fulfill.isPending ? <Loader2 className="animate-spin" /> : <PackageCheck />}
            Post {staged.length ? `(${staged.length})` : ""}
          </Button>
        }
      />

      <div className="space-y-3 text-xs">
        <p className="text-muted-foreground">
          {so.client_code} · {so.client_name} · pick batches → stage → post (issues inventory)
        </p>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Material</TableHead>
                <TableHead className="text-right">Ordered</TableHead>
                <TableHead className="text-right">Fulfilled</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {openLines.map((line) => (
                <LineRow
                  key={line.id}
                  line={line}
                  selected={activeLine?.id === line.id}
                  onSelect={() => setActiveLineId(line.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>

        {activeLine ? (
          <section className="rounded border p-3 space-y-2">
            <h2 className="font-medium">
              Allocate line {activeLine.line_no} · {activeLine.material_name}
            </h2>
            <FieldGrid>
              <Field label="Batch" span={4}>
                <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
                  <option value="">Select batch…</option>
                  {matchingBatches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.batch_no} · {b.location_code} · {b.weight} kg avail
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Weight (kg)" span={2}>
                <Input
                  type="number"
                  step="0.001"
                  min={0}
                  className="fs-no-spin"
                  value={weight}
                  onWheel={blockNumberWheel}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={String(remainingWeight(activeLine))}
                />
              </Field>
              <Field label="Qty" span={2} hint={activeLine.qty > 0 ? "Pieces" : "Optional"}>
                <Input
                  type="number"
                  step="1"
                  min={0}
                  className="fs-no-spin"
                  value={qty}
                  onWheel={blockNumberWheel}
                  onChange={(e) => setQty(e.target.value)}
                  disabled={activeLine.qty <= 0}
                />
              </Field>
              <Field label=" " span={2}>
                <Button type="button" size="sm" variant="outline" onClick={addToStage}>
                  <Plus />
                  Add
                </Button>
              </Field>
            </FieldGrid>
            {!matchingBatches.length ? (
              <p className="text-2xs text-muted-foreground">No matching in-stock batches for this line.</p>
            ) : null}
          </section>
        ) : null}

        {staged.length ? (
          <section className="rounded border p-3">
            <h2 className="mb-2 font-medium">Staged ({staged.length})</h2>
            <ul className="space-y-1">
              {staged.map((s, i) => {
                const line = so.lines?.find((l) => l.id === s.sales_order_line_id);
                const batch = matchingBatches.find((b) => b.id === s.inventory_batch_id);
                return (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span>
                      L{line?.line_no} · {batch?.batch_no ?? s.inventory_batch_id.slice(0, 8)} ·{" "}
                      {s.weight} kg
                      {s.quantity > 0 ? ` · ${s.quantity} pcs` : ""}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setStaged((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <Trash2 />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {history.length ? (
          <section className="rounded border p-3">
            <h2 className="mb-2 font-medium">Prior allocations</h2>
            <ul className="space-y-1 text-muted-foreground">
              {history.slice(0, 10).map((h) => (
                <li key={h.id}>
                  L{h.line_no} · {h.batch_no} · {h.weight} kg ·{" "}
                  {new Date(h.fulfilled_at).toLocaleString()}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <DocumentStickyBar>
        <span>
          Staged <strong className="tabular-nums">{staged.length}</strong> ·{" "}
          <strong className="tabular-nums">{totalStagedWeight.toFixed(3)}</strong> kg
        </span>
      </DocumentStickyBar>
    </DocumentPage>
  );
}

function LineRow({
  line,
  selected,
  onSelect,
}: {
  line: SalesOrderLine;
  selected: boolean;
  onSelect: () => void;
}) {
  const remWt = remainingWeight(line);
  const remQty = remainingQty(line);
  const ordered = line.weight > 0 ? `${line.weight} kg` : `${line.qty} pcs`;

  return (
    <TableRow
      className={selected ? "bg-muted/40 cursor-pointer" : "cursor-pointer"}
      onClick={onSelect}
    >
      <TableCell>{line.line_no}</TableCell>
      <TableCell>
        {line.material_name}
        {line.shape_label ? ` · ${line.shape_label}` : ""}
      </TableCell>
      <TableCell className="text-right tabular-nums">{ordered}</TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {line.fulfilled_weight > 0 ? line.fulfilled_weight : "—"}
      </TableCell>
      <TableCell className="text-right tabular-nums font-medium">
        {remWt > 0 ? `${remWt} kg` : remQty > 0 ? `${remQty} pcs` : "—"}
      </TableCell>
    </TableRow>
  );
}
